/**
 * BkPickerField — wiederverwendbares BK-Dropdown-Picker-Formularfeld.
 *
 * Datenquelle (Fix 09/2026): der v2-Matching-Endpoint
 * `GET /v2/admin/matching/{pbUserId}` liefert die Betreuungskräfte als nach
 * Score sortierte Kandidatenliste.
 *
 * Vorher rief das Feld `GET /v1/admin/user/{id}/matching` auf — diese Route
 * existiert nicht (404). Der Catch-Fallback lud dann eine ungefilterte Seite
 * der BK-Liste und filterte clientseitig auf `konto === 4`; auf einer nach ID
 * absteigend sortierten Seite sind das fast nie aktive BKs, weshalb der Picker
 * in der Praxis „keine Betreuungen gefunden" zeigte.
 *
 * Suche: die Kandidatenliste ist begrenzt (LIST_LIMIT), aktive BKs gibt es
 * deutlich mehr. Ab zwei Zeichen fragt das Feld deshalb zusätzlich
 * serverseitig über die BK-Liste (`konto=4`) und mischt die Treffer dazu.
 *
 * Performance: die Basisliste wird pro pbUserId in einem Modul-Level-Cache
 * (Map) gehalten. Wird der Drawer ein zweites Mal für denselben PB geöffnet,
 * erscheint der Picker sofort gefüllt — kein erneuter HTTP-Request.
 * Der Cache wird nach 5 Minuten invalidiert, sodass Aktualisierungen
 * (neue BKs) ohne Hard-Reload sichtbar werden.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import { v1Users, type V1User } from '../../../lib/v1';
import { v2Matching } from '../../../lib/matching';

/* ─── Option-Shape ───────────────────────────────────────────────────────── */

/** Normalisierte Picker-Zeile — gleich, egal aus welcher Quelle sie stammt. */
export interface BkOption {
  id: number;
  name: string;
  email: string | null;
  /** 0…100 aus dem Matching; fehlt bei Treffern aus der Suche/dem Fallback. */
  score?: number;
}

/** Wie viele Matching-Kandidaten initial geladen werden. */
const LIST_LIMIT = 200;
/** Ab wie vielen Zeichen zusätzlich serverseitig gesucht wird. */
const SEARCH_MIN_CHARS = 2;

function nameOfV1User(u: V1User): string {
  const p = u.profiles?.[0];
  const parts = [p?.vorname, p?.nachname].filter(Boolean).join(' ').trim();
  return (u.fullname as string | undefined)?.trim() || parts || `BK #${u.id}`;
}

function fromV1User(u: V1User): BkOption {
  return { id: u.id, name: nameOfV1User(u), email: (u.email as string | null) ?? null };
}

/* ─── Module-level cache ─────────────────────────────────────────────────── */

interface CacheEntry {
  bks: BkOption[];
  expiresAt: number;
}

/** 5-minute TTL — lange genug für eine Admin-Session, kurz genug für frische Daten. */
const BK_CACHE_TTL_MS = 5 * 60 * 1000;
const bkCache = new Map<number, CacheEntry>();

function getCached(pbUserId: number): BkOption[] | null {
  const entry = bkCache.get(pbUserId);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) { bkCache.delete(pbUserId); return null; }
  return entry.bks;
}

function setCache(pbUserId: number, bks: BkOption[]): void {
  bkCache.set(pbUserId, { bks, expiresAt: Date.now() + BK_CACHE_TTL_MS });
}

/** Exposed for testing: clears the module-level cache. */
export function clearBkCache(): void {
  bkCache.clear();
}

/* ─── Laden ──────────────────────────────────────────────────────────────── */

/** Aktive BK-Liste als Fallback, wenn das Matching nicht antwortet. */
async function loadActiveBks(): Promise<BkOption[]> {
  const page = await v1Users.byRole('BK', { perPage: 100, filters: { konto: ['4'] } });
  return (page.data as V1User[]).filter(u => u.status !== 2).map(fromV1User);
}

/** Basisliste: Matching-Kandidaten, sonst aktive BKs. */
export async function loadBkOptions(pbUserId: number): Promise<BkOption[]> {
  try {
    const res = await v2Matching.candidatesFor(pbUserId, LIST_LIMIT);
    const list = (res.candidates ?? []).map(c => ({
      id: c.id, name: c.name || `BK #${c.id}`, email: c.email, score: c.score,
    }));
    if (list.length > 0) return list;
  } catch {
    /* fällt unten auf die BK-Liste zurück */
  }
  return loadActiveBks();
}

/* ─── Props ──────────────────────────────────────────────────────────────── */

export interface BkPickerFieldProps {
  dark: boolean;
  pbUserId: number;
  value: number | null;
  onChange: (id: number | null, name: string | null) => void;
  /**
   * Anzeigename für `value`, falls die gewählte BK weder in der Matching-Liste
   * noch in den Suchtreffern vorkommt (z. B. beim Bearbeiten einer bestehenden
   * Betreuung → `care.bk_name`). Ohne Fallback würde nur der Platzhalter stehen.
   */
  valueLabel?: string | null;
}

/* ─── Component ──────────────────────────────────────────────────────────── */

export function BkPickerField({ dark, pbUserId, value, onChange, valueLabel }: BkPickerFieldProps) {
  const tc = getThemeColors(dark);
  const { t } = useTranslation();

  const cached = getCached(pbUserId);
  const [bks, setBks] = useState<BkOption[]>(cached ?? []);
  const [loading, setLoading] = useState(cached === null);
  const [search, setSearch] = useState('');
  /** Serverseitige Suchtreffer (außerhalb der Basisliste). */
  const [remoteHits, setRemoteHits] = useState<BkOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  /** Zuletzt im Dropdown gewählte BK — Suchtreffer verschwinden nach der Auswahl. */
  const [picked, setPicked] = useState<BkOption | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Fast path: already cached → no HTTP request, no loading state
    const hit = getCached(pbUserId);
    if (hit) {
      setBks(hit);
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);

    loadBkOptions(pbUserId)
      .then(list => {
        if (!alive) return;
        setCache(pbUserId, list);
        setBks(list);
      })
      .catch(() => { /* leere Liste + Leer-Hinweis */ })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
  }, [pbUserId]);

  // Serverseitige Suche über ALLE aktiven BKs — die Basisliste ist gekappt.
  useEffect(() => {
    const q = search.trim();
    if (q.length < SEARCH_MIN_CHARS) { setRemoteHits([]); setSearching(false); return; }

    let alive = true;
    setSearching(true);
    const timer = window.setTimeout(() => {
      v1Users.byRole('BK', { perPage: 25, filters: { konto: ['4'], search: q } })
        .then(page => {
          if (!alive) return;
          setRemoteHits((page.data as V1User[]).filter(u => u.status !== 2).map(fromV1User));
        })
        .catch(() => { if (alive) setRemoteHits([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 300);

    return () => { alive = false; window.clearTimeout(timer); };
  }, [search]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo<BkOption[]>(() => {
    const q = search.trim().toLowerCase();
    const local = q
      ? bks.filter(bk =>
          bk.name.toLowerCase().includes(q)
          || String(bk.id).includes(q)
          || (bk.email ?? '').toLowerCase().includes(q))
      : bks;
    if (remoteHits.length === 0) return local;
    // Serverseitige Treffer anhängen, ohne Dubletten.
    const seen = new Set(local.map(o => o.id));
    return [...local, ...remoteHits.filter(o => !seen.has(o.id))];
  }, [bks, remoteHits, search]);

  const selectedBk = value == null
    ? undefined
    : bks.find(bk => bk.id === value)
      ?? remoteHits.find(bk => bk.id === value)
      ?? (picked?.id === value ? picked : undefined);
  // valueLabel nur als Fallback für die ursprünglich übergebene BK: sobald im
  // Picker eine andere BK gewählt wurde (picked), greift er nicht mehr.
  const fallbackName = value != null && valueLabel && (picked === null || picked.id === value)
    ? valueLabel
    : null;
  const selectedName = selectedBk ? selectedBk.name : fallbackName;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', padding: '8px 12px',
          border: `1px solid ${tc.border}`, borderRadius: 8,
          background: tc.inputBg, color: value ? tc.heading : tc.muted,
          fontSize: 13, cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
        }}
      >
        {/* Only show "Loading" text if actually loading and list is empty */}
        {loading && bks.length === 0
          ? t('pflege.cares.create_bk_loading')
          : selectedName ?? t('pflege.cares.create_bk_placeholder')}
        {loading && bks.length > 0 && (
          <span style={{ marginLeft: 8, fontSize: 10, color: tc.muted }}>…</span>
        )}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: tc.card, border: `1px solid ${tc.border}`, borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)', marginTop: 4,
          maxHeight: 220, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: `1px solid ${tc.border}` }}>
            <input
              autoFocus
              type="text"
              placeholder={t('pflege.cares.create_bk_placeholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', fontSize: 12.5, padding: '6px 8px',
                border: `1px solid ${tc.border}`, borderRadius: 6, background: tc.inputBg,
                color: tc.text, outline: 'none', fontFamily: 'Outfit,sans-serif',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {/* Dezenter Inline-Ladehinweis statt prominenter Text */}
            {(loading || searching) && filtered.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: 12, color: tc.muted }}>
                {t('pflege.cares.create_bk_loading')}
              </div>
            )}
            {!loading && !searching && filtered.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: 12, color: tc.muted }}>
                {t('pflege.cares.create_bk_empty')}
              </div>
            )}
            {filtered.map(bk => {
              const isSelected = bk.id === value;
              return (
                <button
                  key={bk.id}
                  type="button"
                  onClick={() => { setPicked(bk); onChange(bk.id, bk.name); setOpen(false); setSearch(''); }}
                  style={{
                    width: '100%', textAlign: 'left', padding: '9px 12px',
                    background: isSelected ? BRAND_LIGHT : 'transparent',
                    color: isSelected ? BRAND : tc.text,
                    border: 'none', cursor: 'pointer', fontSize: 13,
                    fontWeight: isSelected ? 600 : 400,
                    fontFamily: 'Outfit,sans-serif',
                    borderBottom: `1px solid ${tc.borderSoft}`,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                >
                  <span style={{ fontWeight: 500, flex: 1 }}>{bk.name}</span>
                  {bk.score !== undefined && (
                    <span style={{ fontSize: 11, color: tc.muted }}>{bk.score}%</span>
                  )}
                  <span style={{ fontSize: 11, color: tc.muted }}>#{bk.id}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
